import { FrontendConstants } from '../common/FrontendConstants';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import type { IKnowledgeDocumentDTO } from '@shared/common';

/** Multipart form field name the lobby service expects for the uploaded knowledge file. */
const localConstants = { fileField: 'file' } as const;

/**
 * Client for knowledge-base documents.
 *
 * Tenant-facing reads/writes route through the lobby service dashboard surface (`dashboard/:tenantID/knowledge`).
 */
export class KBClient {
  /** Singleton instance. */
  private static instance: KBClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton KBClient instance. */
  public static GetInstance(): KBClient {
    return KBClient.instance ?? (KBClient.instance = new KBClient());
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/knowledge - List a tenant's knowledge documents.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The tenant's knowledge documents, most recent first.
   */
  public async ListDocuments(signal: AbortSignal, tenantID: string): Promise<IKnowledgeDocumentDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/knowledge`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch knowledge documents');
    return await res.json();
  }

  /**
   * POST /api/lobby/dashboard/:tenantID/knowledge - Upload a new knowledge document (multipart).
   *
   * The browser sets the multipart `Content-Type` boundary automatically, so no content-type header is supplied.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param file - The file to upload.
   * @param title - Optional document title; defaults to the file name server-side when omitted.
   * @returns The created knowledge document.
   */
  public async UploadDocument(signal: AbortSignal, tenantID: string, file: File, title?: string): Promise<IKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const form = new FormData();
    form.append(localConstants.fileField, file);
    if (title) form.append('title', title);
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/knowledge`, { method: FrontendConstants.MethodPost, body: form, signal }, auth);
    if (!res.ok) throw new Error('Failed to upload knowledge document');
    return await res.json();
  }

  /**
   * POST /api/lobby/dashboard/:tenantID/knowledge/manual - Create a manually-authored knowledge document from a typed title and body.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param title - The document title.
   * @param body - The multiline body text.
   * @returns The created knowledge document.
   */
  public async CreateManualDocument(signal: AbortSignal, tenantID: string, title: string, body: string): Promise<IKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `dashboard/${tenantID}/knowledge/manual`,
      { method: FrontendConstants.MethodPost, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, body: JSON.stringify({ title, body }), signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to create knowledge document');
    return await res.json();
  }

  /**
   * PUT /api/lobby/dashboard/:tenantID/knowledge/:documentID/manual - Update a manually-authored knowledge document's title and body.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param documentID - The document to update.
   * @param title - The new document title.
   * @param body - The new multiline body text.
   * @returns The updated knowledge document.
   */
  public async UpdateManualDocument(signal: AbortSignal, tenantID: string, documentID: string, title: string, body: string): Promise<IKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `dashboard/${tenantID}/knowledge/${documentID}/manual`,
      { method: FrontendConstants.MethodPut, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, body: JSON.stringify({ title, body }), signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to update knowledge document');
    return await res.json();
  }

  /**
   * DELETE /api/lobby/dashboard/:tenantID/knowledge/:documentID - Remove a knowledge document.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param documentID - The document to remove.
   */
  public async DeleteDocument(signal: AbortSignal, tenantID: string, documentID: string): Promise<void> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/knowledge/${documentID}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new Error('Failed to delete knowledge document');
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/knowledge/:documentID/content - Fetch a knowledge document's original uploaded bytes.
   *
   * Returned as a Blob (typed by the server's Content-Type) so callers can open it in a new tab via an object URL - the endpoint is auth-gated, so a plain `<a href>` cannot carry the bearer
   * token.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param documentID - The document whose content to fetch.
   * @returns The document content as a typed Blob.
   */
  public async GetDocumentContent(signal: AbortSignal, tenantID: string, documentID: string): Promise<Blob> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/knowledge/${documentID}/content`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch knowledge document content');
    return await res.blob();
  }

  /**
   * GET /api/lobby/admin/tenants/:tenantID/knowledge - List a tenant's knowledge documents from the operator console (Portal). Authorized as a privileged operator rather than a tenant member.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The tenant's knowledge documents, most recent first.
   */
  public async ListAdminDocuments(signal: AbortSignal, tenantID: string): Promise<IKnowledgeDocumentDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}/knowledge`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch knowledge documents');
    return await res.json();
  }

  /**
   * POST /api/lobby/admin/tenants/:tenantID/knowledge - Upload a knowledge document on a tenant's behalf from the operator console (Portal).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param file - The file to upload.
   * @param title - Optional document title; defaults to the file name server-side when omitted.
   * @returns The created knowledge document.
   */
  public async UploadAdminDocument(signal: AbortSignal, tenantID: string, file: File, title?: string): Promise<IKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const form = new FormData();
    form.append(localConstants.fileField, file);
    if (title) form.append('title', title);
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}/knowledge`, { method: FrontendConstants.MethodPost, body: form, signal }, auth);
    if (!res.ok) throw new Error('Failed to upload knowledge document');
    return await res.json();
  }

  /**
   * POST /api/lobby/admin/tenants/:tenantID/knowledge/manual - Create a manually-authored knowledge document on a tenant's behalf (operator console).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param title - The document title.
   * @param body - The multiline body text.
   * @returns The created knowledge document.
   */
  public async CreateAdminManualDocument(signal: AbortSignal, tenantID: string, title: string, body: string): Promise<IKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `admin/tenants/${tenantID}/knowledge/manual`,
      { method: FrontendConstants.MethodPost, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, body: JSON.stringify({ title, body }), signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to create knowledge document');
    return await res.json();
  }

  /**
   * PUT /api/lobby/admin/tenants/:tenantID/knowledge/:documentID/manual - Update a manually-authored knowledge document on a tenant's behalf (operator console).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param documentID - The document to update.
   * @param title - The new document title.
   * @param body - The new multiline body text.
   * @returns The updated knowledge document.
   */
  public async UpdateAdminManualDocument(signal: AbortSignal, tenantID: string, documentID: string, title: string, body: string): Promise<IKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `admin/tenants/${tenantID}/knowledge/${documentID}/manual`,
      { method: FrontendConstants.MethodPut, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, body: JSON.stringify({ title, body }), signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to update knowledge document');
    return await res.json();
  }

  /**
   * DELETE /api/lobby/admin/tenants/:tenantID/knowledge/:documentID - Remove a tenant's knowledge document from the operator console (Portal).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param documentID - The document to remove.
   */
  public async DeleteAdminDocument(signal: AbortSignal, tenantID: string, documentID: string): Promise<void> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}/knowledge/${documentID}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new Error('Failed to delete knowledge document');
  }

  /**
   * GET /api/lobby/admin/tenants/:tenantID/knowledge/:documentID/content - Fetch a tenant's knowledge document bytes from the operator console (Portal).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param documentID - The document whose content to fetch.
   * @returns The document content as a typed Blob.
   */
  public async GetAdminDocumentContent(signal: AbortSignal, tenantID: string, documentID: string): Promise<Blob> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}/knowledge/${documentID}/content`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch knowledge document content');
    return await res.blob();
  }
}
